import {
  AppShell,
  Avatar,
  Box,
  Burger,
  Group,
  Menu,
  NavLink as MantineNavLink,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import {
  IconChevronDown,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconLogout,
  IconUserCircle,
} from "@tabler/icons-react";
import type { IconProps } from "@tabler/icons-react";
import type { ComponentType, ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/useAuth";
import { axioTokens } from "../theme";
import { isNavActive, type NavItemConfig } from "../utils/navActive";
import { BrandLogo } from "./BrandLogo";

const SIDEBAR_EXPANDED_WIDTH = "var(--axio-unit-240)";
const SIDEBAR_COLLAPSED_WIDTH = "var(--axio-unit-72)";
const SIDEBAR_STORAGE_KEY = "axiohire-sidebar-collapsed";
const SIDEBAR_TRANSITION_MS = Number.parseInt(axioTokens.duration.medium, 10);
const COLLAPSE_SIDEBAR_LABEL = "Collapse sidebar";
const EXPAND_SIDEBAR_LABEL = "Expand sidebar";
const HEADER_LOGO_HEIGHT = axioTokens.unit[24];
const HEADER_DIVIDER_HEIGHT = "var(--axio-unit-18)";
const NAV_ITEM_HEIGHT = "var(--axio-unit-40)";
const NAV_ITEM_GAP = "var(--axio-unit-8)";

export interface RoleShellNavItem extends NavItemConfig {
  label: string;
  icon: ComponentType<IconProps>;
  color?: string;
  badge?: boolean;
  badgeLabel?: string;
  badgeColor?: string;
  attention?: boolean;
}

export interface RoleShellNavSection {
  label?: string;
  items: RoleShellNavItem[];
}

interface RoleShellProps {
  portalLabel: string;
  logoTo: string;
  navItems?: RoleShellNavItem[];
  navSections?: RoleShellNavSection[];
  headerExtra?: ReactNode;
  navbarExtra?: ReactNode;
  profileTo?: string;
  avatarColor?: string;
  avatarFallback?: string;
}

function renderNavItem(
  item: RoleShellNavItem,
  location: ReturnType<typeof useLocation>,
  opened: boolean,
  close: () => void,
  iconRail: boolean,
) {
  const navProps = {
    to: item.to,
    label: item.label,
    leftSection: <item.icon size="var(--axio-unit-18)" stroke="var(--axio-stroke-width-subtle)" />,
    rightSection: item.badge ? (
      <Box
        component="span"
        aria-label={item.badgeLabel}
        className={`kycStatusDot kycStatusDot-${item.badgeColor ?? "orange"}${item.attention ? " kycPulseBadge" : ""}`}
      />
    ) : undefined,
    active: isNavActive(location.pathname, item),
    variant: "subtle" as const,
    color: item.color ?? "gray",
    mb: NAV_ITEM_GAP,
    className: `frameNavLink${item.attention ? " frameNavLinkAttention" : ""}${iconRail ? " frameNavLinkCollapsed" : ""}`,
    styles: {
      root: {
        borderRadius: "var(--axio-radius)",
        fontWeight: "var(--axio-font-weight-medium)",
        height: NAV_ITEM_HEIGHT,
        paddingLeft: iconRail ? "var(--axio-unit-0)" : "var(--axio-unit-12)",
        paddingRight: iconRail ? "var(--axio-unit-0)" : undefined,
      },
      label: {
        fontSize: "var(--axio-font-size-sm)",
      },
    },
    onClick: () => {
      if (opened) close();
    },
  };

  const link = item.exclude ? (
    <MantineNavLink key={item.to} component={Link} {...navProps} />
  ) : (
    <MantineNavLink
      key={item.to}
      component={NavLink}
      end={Boolean(item.end || item.exact)}
      {...navProps}
    />
  );

  if (!iconRail) return link;

  return (
    <Tooltip key={item.to} label={item.label} position="right" withArrow>
      <Box>{link}</Box>
    </Tooltip>
  );
}

export function RoleShell({
  portalLabel,
  logoTo,
  navItems,
  navSections,
  headerExtra,
  navbarExtra,
  profileTo,
  avatarColor = "violet",
  avatarFallback = "U",
}: RoleShellProps) {
  const [opened, { toggle, close }] = useDisclosure();
  const [desktopCollapsed, setDesktopCollapsed] = useLocalStorage({
    key: SIDEBAR_STORAGE_KEY,
    defaultValue: false,
  });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const sections: RoleShellNavSection[] =
    navSections ?? (navItems ? [{ items: navItems }] : []);
  const sidebarToggleLabel = desktopCollapsed ? EXPAND_SIDEBAR_LABEL : COLLAPSE_SIDEBAR_LABEL;

  return (
    <AppShell
      header={{ height: axioTokens.frameHeaderHeight }}
      navbar={{
        width: desktopCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH,
        breakpoint: "sm",
        collapsed: { mobile: !opened },
      }}
      padding={{ base: "md", sm: "lg", md: "xl" }}
      transitionDuration={SIDEBAR_TRANSITION_MS}
    >
      <AppShell.Header p="var(--axio-unit-0)">
        <Group h="100%" px="lg" justify="space-between" align="center" wrap="nowrap">
          <Group gap="var(--axio-unit-12)" align="center" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" color="white" />
            <BrandLogo variant="wordmark" scheme="dark" height={HEADER_LOGO_HEIGHT} to={logoTo} />
            <Box
              style={{
                width: "var(--axio-unit-1)",
                height: HEADER_DIVIDER_HEIGHT,
                background: axioTokens.frameLine,
                flexShrink: "var(--axio-number-zero)",
              }}
            />
            <Text size="sm" style={{ color: axioTokens.frameLabel }} fw="var(--axio-font-weight-medium)" lh="var(--axio-line-height-compact)">
              {portalLabel}
            </Text>
          </Group>
          <Group gap="sm">
            {headerExtra}
            <Menu shadow="md" width={axioTokens.unit[240]} position="bottom-end">
              <Menu.Target>
                <UnstyledButton
                  style={{
                    padding: "var(--axio-unit-4) var(--axio-unit-6) var(--axio-unit-4) var(--axio-unit-4)",
                    borderRadius: "var(--axio-radius)",
                    transition: "var(--axio-transition-interactive)",
                  }}
                  className="frameUserMenu"
                >
                  <Group gap="var(--axio-unit-8)">
                    <Avatar
                      color={avatarColor}
                      radius={axioTokens.radiusCircle}
                      size="var(--axio-unit-28)"
                    >
                      {user?.full_name?.[0]?.toUpperCase() ?? avatarFallback}
                    </Avatar>
                    <Box visibleFrom="xs">
                      <Text size="sm" fw="var(--axio-font-weight-semibold)" lh="var(--axio-line-height-compact)" c="white">
                        {user?.full_name}
                      </Text>
                    </Box>
                    <IconChevronDown size="var(--axio-unit-14)" color={axioTokens.frameLabel} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                {user?.email && (
                  <>
                    <Menu.Label>{user.email}</Menu.Label>
                    <Menu.Divider />
                  </>
                )}
                {profileTo && (
                  <>
                    <Menu.Item
                      leftSection={<IconUserCircle size="var(--axio-unit-16)" />}
                      onClick={() => navigate(profileTo)}
                    >
                      My Profile
                    </Menu.Item>
                    <Menu.Divider />
                  </>
                )}
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size="var(--axio-unit-16)" />}
                  onClick={() => {
                    logout();
                    navigate("/login");
                  }}
                >
                  Log out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar>
        <AppShell.Section
          grow
          component={ScrollArea}
          type="hover"
          scrollbarSize={axioTokens.unit[4]}
          px={desktopCollapsed ? "xs" : "md"}
          pt="md"
          pb={navbarExtra && !desktopCollapsed ? "sm" : "md"}
        >
          <Stack gap="var(--axio-unit-0)">
            {sections.map((section, sectionIndex) => (
              <Box key={section.label ?? sectionIndex}>
                {section.label && !desktopCollapsed ? <p className="navSectionLabel">{section.label}</p> : null}
                {section.items.map((item) =>
                  renderNavItem(item, location, opened, close, desktopCollapsed),
                )}
              </Box>
            ))}
          </Stack>
        </AppShell.Section>
        {navbarExtra && !desktopCollapsed ? (
          <AppShell.Section px="md" pb="md">
            <Box className="navbarExtraCard">{navbarExtra}</Box>
          </AppShell.Section>
        ) : null}
        <AppShell.Section visibleFrom="sm" px={desktopCollapsed ? "xs" : "md"} pb="md">
          <Tooltip
            label={sidebarToggleLabel}
            position="right"
            withArrow
            disabled={!desktopCollapsed}
          >
            <UnstyledButton
              className={`frameSidebarToggle${desktopCollapsed ? " frameSidebarToggleCollapsed" : ""}`}
              aria-label={sidebarToggleLabel}
              onClick={() => setDesktopCollapsed((collapsed) => !collapsed)}
            >
              {desktopCollapsed ? (
                <IconLayoutSidebarLeftExpand size="var(--axio-unit-18)" stroke="var(--axio-stroke-width-subtle)" />
              ) : (
                <IconLayoutSidebarLeftCollapse size="var(--axio-unit-18)" stroke="var(--axio-stroke-width-subtle)" />
              )}
              {desktopCollapsed ? null : (
                <Text size="sm" fw="var(--axio-font-weight-medium)" lh="var(--axio-line-height-compact)">
                  {sidebarToggleLabel}
                </Text>
              )}
            </UnstyledButton>
          </Tooltip>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Box className="pageContainer">
          <Outlet />
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}
